import { useState } from "react";
import { api } from "@/lib/api";
import { KeyRound, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

/**
 * Inline password-change card rendered on the Profile page. Calls
 * POST /api/auth/me/change-password. Sets `password_locked` server-side so
 * the startup hook never resets the password back to the env value.
 */
export default function ChangePasswordCard() {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) {
      toast.error("New password and confirmation don't match.");
      return;
    }
    if (next.length < 8) {
      toast.error("New password must be at least 8 characters.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/me/change-password", { current_password: cur, new_password: next });
      setCur(""); setNext(""); setConfirm("");
      toast.success("Password updated. Use the new one next time you sign in.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not change password");
    } finally { setBusy(false); }
  };

  return (
    <div className="jj-card mt-6 p-6 sm:p-7" data-testid="change-password-card">
      <div className="overline text-burgundy flex items-center gap-2"><KeyRound className="h-3.5 w-3.5" /> Security</div>
      <h2 className="font-serif text-2xl text-jjblue mt-1">Change your password</h2>
      <p className="mt-1.5 text-sm text-[var(--jj-text-secondary)] max-w-md">
        Pick something only you would know. Once you save, this becomes the master key —
        the deployment defaults can no longer override it.
      </p>

      <form onSubmit={submit} className="mt-5 space-y-3 max-w-md">
        <div>
          <label className="text-xs uppercase tracking-wider text-burgundy">Current password</label>
          <div className="relative mt-1">
            <input
              type={show ? "text" : "password"}
              value={cur}
              onChange={(e) => setCur(e.target.value)}
              className="input-jj pr-10"
              required
              autoComplete="current-password"
              data-testid="cp-current"
            />
            <button
              type="button"
              onClick={() => setShow((v) => !v)}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[var(--jj-muted)] hover:text-burgundy"
              tabIndex={-1}
              aria-label={show ? "Hide passwords" : "Show passwords"}
            >
              {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-burgundy">New password</label>
          <input
            type={show ? "text" : "password"}
            value={next}
            onChange={(e) => setNext(e.target.value)}
            className="input-jj mt-1"
            required
            minLength={8}
            autoComplete="new-password"
            data-testid="cp-new"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-burgundy">Confirm new password</label>
          <input
            type={show ? "text" : "password"}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="input-jj mt-1"
            required
            minLength={8}
            autoComplete="new-password"
            data-testid="cp-confirm"
          />
        </div>
        <button type="submit" disabled={busy} className="btn-primary !py-2.5 !px-5 text-sm" data-testid="cp-submit">
          {busy ? "Updating…" : "Save new password"}
        </button>
      </form>
    </div>
  );
}
